import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatButton } from '@framework/ui/atoms';
import { Loading } from '@framework/ui/atoms/Loading';
import { trpcClient } from '@framework/trpcClient';

type Status = 'loading' | 'ok' | 'invalid';

type EmailInfo = {
  previousMailAddress: string;
  newMailAddress: string;
};

type Props = {
  token: string;
  onConfirmSuccess(): void;
};

/**
 * メールアドレス変更(EPIC-542)の確認メール内リンクを開いた際の確認フォーム。
 * 未ログインでも表示できる(確認リンクを別デバイス/未ログインのブラウザで開く場合があるため)。
 *
 * 自前発行の確認トークンを使うため、Firebase Authのaction codeのように「適用した時点で
 * 消費済みになる」制約が無い。confirmMailAddressChangeが失敗してもトークンは有効なままなので、
 * 再試行は同じボタンを再度押すだけでよい。
 */
export const ConfirmForm: React.FC<Props> = ({ token, onConfirmSuccess }: Props) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>('loading');
  const [emailInfo, setEmailInfo] = useState<EmailInfo | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    trpcClient.user.auth.fetchMailAddressChangeToken
      .query({ token })
      .then((result) => {
        if (result.status !== 'ok' || !result.previousMailAddress || !result.newMailAddress) {
          setStatus('invalid');
          return;
        }
        setEmailInfo({ previousMailAddress: result.previousMailAddress, newMailAddress: result.newMailAddress });
        setStatus('ok');
      })
      .catch((error) => {
        console.error('Failed to fetch mail address change token:', error);
        setStatus('invalid');
      });
  }, [token]);

  const handleConfirm = useCallback(async () => {
    setProcessing(true);
    setError('');

    try {
      const { result } = await trpcClient.user.auth.confirmMailAddressChange.mutate({ token });

      if (result === 'success') {
        onConfirmSuccess();
        return;
      }

      setStatus('invalid');
    } catch (error) {
      // トークンは未消費のままなので、エラー表示を出して再度ボタンを押せば再試行できる。
      setError(t('auth.mailAddressConfirm.confirmError'));
      console.error('Failed to confirm mail address change:', error);
    } finally {
      setProcessing(false);
    }
  }, [token, onConfirmSuccess, t]);

  if (status === 'loading') {
    return <Loading />;
  }

  if (status === 'invalid' || !emailInfo) {
    return <div className="py-4">{t('auth.mailAddressConfirm.invalidLink')}</div>;
  }

  return (
    <>
      <div className="py-4">
        {t('auth.mailAddressConfirm.confirmDescription', {
          previousMailAddress: emailInfo.previousMailAddress,
          newMailAddress: emailInfo.newMailAddress,
        })}
      </div>

      <div className="pb-8 pt-16 text-center">
        <FlatButton className="px-16" color="brand" onClick={handleConfirm} disabled={processing} loading={processing}>
          {t('auth.mailAddressConfirm.confirmButton')}
        </FlatButton>
        {error && <div className="pt-4 text-red-700">{error}</div>}
      </div>
    </>
  );
};
